import { useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { postPalmVerificationPreflight } from "../api/apis";
import { Nav } from "../components/Nav";
import { PalmVideoFrame } from "../components/scan/PalmVideoFrame";

import { getPayloadWithSession } from "../api/sessionClient";
import { DemoMode } from "../components/scan/DemoMode";
import {
  PalmTouchCodeSuccess,
  ScanBottom,
} from "../components/scan/ScanBottom";
import { ScanProgress } from "../components/scan/ScanProgress";
import { SwitchLeftPalm } from "../components/scan/SwitchLeftPalm";
import { VerifyChecking } from "../components/scan/VerifyChecking";
import { WorkThrough } from "../components/scan/WorkThrough";
import { getCodeMessageFromError, messageFromError } from "../i18n/errorMessage";
import { PALM_LEFT, PALM_RIGHT } from "../lib/constants";
import { decodeBase64 } from "../lib/cryptoUtils";
import { redirectBackWithError } from "../lib/oauthUtils";
import {
  createUser,
  getPalm64FromResult,
  handleScanAction,
  saveLastPalmSide,
  sideListFromAction,
  type IScanAction,
} from "../lib/palmHelp";
import { preloadScanPageImages } from "../lib/preloadHelp";
import { captureError } from "../lib/sentryUtils";
import {
  isDemoVersion,
  isLessTime,
  isSupportWasmSIMD,
  isUseGesture,
  sleep,
  videoAspectRatio,
} from "../lib/utils";
import { cleanupVideoUtils, getFrame } from "../lib/videoUtils";
import createPalmModule from "../palmid_wasm/PalmAPICapture.mjs";
import { getIdentityCommitment, isLoggedIn, removeUser } from "../store/user";
import {
  type PalmCaptureResult,
  type PalmModule,
  type PalmTouchPoints,
} from "../types/palm";
import { PalmCaptureStatusMap } from "../types/palmEnumString";

const totalProgress = 6;
const cdnUrl = "https://assets.very.org/static/wasm/v1/PalmAPICapture.wasm";
let livenessCheckCount = 0;

const isGesture = isUseGesture();

// Helper function to preload WASM file into Service Worker cache
const preloadWasmToCache = async () => {
  if ("serviceWorker" in navigator && "caches" in window) {
    try {
      const cache = await caches.open("very-wasm-cache-v1");
      const cached = await cache.match(cdnUrl);
      if (!cached) {
        // Preload WASM file into cache
        await cache.add(cdnUrl).catch((error) => {
          console.warn("Failed to preload WASM to cache:", error);
        });
      }
    } catch (error) {
      console.warn("Failed to access cache:", error);
    }
  }
};

// Helper function to clear corrupted WASM cache
const clearWasmCache = async () => {
  if ("caches" in window) {
    try {
      const cache = await caches.open("very-wasm-cache-v1");
      await cache.delete(cdnUrl);
      console.log("[WASM] Cleared corrupted WASM cache");
    } catch (error) {
      console.warn("[WASM] Failed to clear WASM cache:", error);
    }
  }
};

const getPalmModule = async (): Promise<PalmModule> => {
  try {
    // Set timeout to prevent hanging
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error("get palm module timeout"));
      }, 20000);
    });

    const modulePromise = createPalmModule({
      locateFile: (path: string) => {
        if (path === "PalmAPICapture.wasm") {
          return cdnUrl;
        }
        return path;
      },
    });

    return await Promise.race([modulePromise, timeoutPromise]);
  } catch (error) {
    // Check if it's a WebAssembly compilation error
    const isWasmError =
      error instanceof WebAssembly.CompileError ||
      error instanceof WebAssembly.RuntimeError ||
      (error instanceof Error &&
        (error.message.includes("WebAssembly") ||
          error.message.includes("wasm") ||
          error.message.includes("can't get Function local")));

    if (isWasmError) {
      console.error("[WASM] WebAssembly compilation error detected:", error);
      captureError("WebAssembly compilation error - clearing cache", error);

      // Clear potentially corrupted cache
      await clearWasmCache();

      // Try loading from local file as fallback
      try {
        const localWasmPath = new URL(
          "../palmid_wasm/PalmAPICapture.wasm",
          import.meta.url
        ).href;
        console.log("[WASM] Attempting to load from local file");
        return await createPalmModule({
          locateFile: (path: string) => {
            if (path === "PalmAPICapture.wasm") {
              return localWasmPath;
            }
            return path;
          },
        });
      } catch (localError) {
        captureError("Failed to load WASM from local file", localError);
        throw new Error(
          "Failed to load WebAssembly module. Please refresh the page or clear your browser cache."
        );
      }
    }

    captureError("Failed to load WASM from CDN, using local file", error);
    const localWasmPath = new URL(
      "../palmid_wasm/PalmAPICapture.wasm",
      import.meta.url
    ).href;
    return await createPalmModule({
      locateFile: (path: string) => {
        if (path === "PalmAPICapture.wasm") {
          return localWasmPath;
        }
        return path;
      },
    });
  }
};

// Helper function to verify return codes
function verifyCode(module: PalmModule, code: number): boolean {
  if (code !== module.ePalmStatus.ePalm_Success) {
    console.error(`Error code: ${code}`);
    return false;
  }
  return true;
}

export const Scan = () => {
  const { t } = useTranslation();
  const isDemo = isDemoVersion();
  const [progress, setProgress] = useState(0);

  const [needGesture, setNeedGesture] = useState<number>(0);
  const navigate = useNavigate();
  const hasStarted = useRef(false);
  const urlParams = new URLSearchParams(window.location.search);
  const action = urlParams.get("action") as IScanAction;

  const sessionId = urlParams.get("sessionId") || urlParams.get("session_id");
  const user_id = urlParams.get("userId") || urlParams.get("user_id");
  const key = urlParams.get("key") || urlParams.get("key");
  const cb = urlParams.get("cb");

  let needPalmSideList = sideListFromAction(action);

  const isNeedCreateFirst =
    !isLoggedIn() && action === "oauth" && (!user_id || user_id === "null");
  const isCreateRef = useRef(
    isNeedCreateFirst || action === "create"
  );
  const [showWorkThrough, setShowWorkThrough] = useState(isCreateRef.current);
  const [showSwitchLeftAnimation, setShowSwitchLeftAnimation] = useState(false);
  const needScanningRef = useRef(true);

  const [status, setStatus] = useState<string>("");
  const [adjustment, setAdjustment] = useState<number>(-1);
  const [palmSide, setPalmSide] = useState<number>(needPalmSideList[0]);
  const [scanSide, setScanSide] = useState<number>(-1);

  const [loading, setLoading] = useState(false);
  const [showLoadWasmMask, setShowLoadWasmMask] = useState(false);

  const [touchPoints, setTouchPoints] = useState<PalmTouchPoints | null>(null);

  const networkError = () => {
    navigate("/verify-failed", {
      state: {
        error: t("common.networkError"),
      },
    });
  };

  const getCaptureResult = async (
    Module: PalmModule,
    option: {
      allow_audit: boolean;
      needSide: number;
      nonce: string;
      isVerify: boolean;
    }
  ): Promise<PalmCaptureResult | undefined> => {
    const { allow_audit, needSide, isVerify } = option;

    const nonce = new Module.PalmNonce();
    const nonceBytes = new Uint8Array(decodeBase64(option.nonce));
    nonce.setBytes(nonceBytes);
    // const bytes = nonce.getBytes();

    let ret = Module.PalmID_Capture(
      Module.PalmStrictness.PalmStrictness_Enrollment_Unique,
      isGesture
        ? Module.PalmLiveness.PalmLiveness_Gesture
        : Module.PalmLiveness.PalmLiveness_Touch,
      nonce
    );

    let status = Module.PalmCaptureStatus.PalmCaptureStatus_Idle;
    const palmDetected = new Module.PalmDetected();
    let lastVerifyPalmSide = -1;

    while (status !== Module.PalmCaptureStatus.PalmCaptureStatus_Success) {
      if (!needScanningRef.current) {
        await sleep(500);
        continue;
      }
      await sleep(2);

      if (!location.href.includes("scan")) {
        break;
      }

      // Create a PalmCaptureStatusWrapper object
      const palmCaptureStatus = new Module.PalmCaptureStatusWrapper();
      const frame = await getFrame(Module);
      if (!frame) {
        // console.error("getFrame failed");
        await sleep(100);
        continue;
      }

      const g = Module.PalmID_GetGesture();
      const p = Module.PalmID_GetTouchPoints();
      setTouchPoints(p);
      ret = Module.PalmID_ProcessFrame(frame, palmDetected, palmCaptureStatus);

      const scanSide = Math.floor(palmDetected.metrics.sidedness * 2);

      // auto switch side for verify
      if (
        isVerify &&
        (palmDetected.metrics.sidedness < 0.3 ||
          palmDetected.metrics.sidedness > 0.7)
      ) {
        lastVerifyPalmSide = scanSide;
        setPalmSide(scanSide);
      }

      if (
        palmDetected.metrics.sidedness !== 0.5 &&
        !isVerify &&
        scanSide !== needSide
      ) {
        if (isLessTime("side error", 3000)) {
          toast(
            needSide === 0
              ? t("scan.pleaseShowLeftHand")
              : t("scan.pleaseShowRightHand")
          );
        }
      } else {
        setNeedGesture(g.value);
      }

      if (g.value) {
        setScanSide(scanSide);
      }

      if (palmDetected.adjustment.value !== 0) {
        setAdjustment(palmDetected.adjustment.value);
      }

      // Retrieve the updated status from the PalmCaptureStatusWrapper object
      status = palmCaptureStatus.getStatus();

      if (status.value === 5) {
        livenessCheckCount++;
        if (livenessCheckCount > 5) {
          navigate("/verify-failed", {
            state: {
              error: t("scan.livenessCheckFailed"),
              isNewCreate: isCreateRef.current,
            },
          });
          return;
        }
      }

      // Status map doesn't need translation as it's internal
      setStatus(PalmCaptureStatusMap[status.value]);

      frame.free();
    }

    // save last verify palm side for verify
    if (isVerify) {
      saveLastPalmSide(lastVerifyPalmSide);
    }

    if (status !== Module.PalmCaptureStatus.PalmCaptureStatus_Success) {
      console.warn(`Palm capture did not succeed. Final status = `, status);
      return;
    }

    const captureResult = new Module.PalmCaptureResult();
    Module.PalmCaptureResult_Create(captureResult);

    ret = Module.PalmID_CaptureResult(captureResult);

    if (!verifyCode(Module, ret)) {
      console.error(
        `PalmID_CaptureResult failed with code: ${JSON.stringify(ret)}`
      );
      return;
    } else {
      if (!isVerify && captureResult.sidedness.value !== needSide) {
        toast.error(t("scan.pleaseShowOtherHand"));

        return getCaptureResult(Module, {
          allow_audit,
          needSide,
          nonce: option.nonce,
          isVerify,
        });
      }

      setTouchPoints({
        code: PalmTouchCodeSuccess,
        thumbTip: { x: 0, y: 0 },
        targetPoint: { x: 0, y: 0 },
      });
      return captureResult;
    }
  };

  const startScan = async () => {
    const supportSIMD = isSupportWasmSIMD();
    if (!supportSIMD) {
      const errorMessage = t("common.browserNotSupported");
      toast.error(errorMessage);
      navigate("/verify-failed", {
        state: {
          error: errorMessage,
        },
      });
      return;
    }
    setShowLoadWasmMask(true);

    let nonce = "";
    let userReviewStatus = "";
    const clientId = urlParams.get("clientId") || urlParams.get("client_id");
    try {
      const nonceResponse = await postPalmVerificationPreflight(
        {
          externalUserId: user_id,
          clientId: clientId,
        },
        AbortSignal.timeout(15000)
      );
      nonce = nonceResponse.nonce;
      userReviewStatus = nonceResponse.status || "";
    } catch (error) {
      captureError("get nonce error", error);
      return;
    }
    if (!nonce) {
      networkError();
      return;
    }

    const preloadSuccess = await preloadScanPageImages();
    if (!preloadSuccess) {
      networkError();
      return;
    }

    const Module = await getPalmModule();

    if (!Module) {
      networkError();
      return;
    }

    setShowLoadWasmMask(false);

    if (action === "oauth" && sessionId) {
      getPayloadWithSession(sessionId);
    }

    const isNeedUpdatePalms = action === "update-palms" || userReviewStatus === "rejected";
    const isNeedCreateFirst = isNeedUpdatePalms ||
      (action === "oauth" && !isLoggedIn() && (!user_id || user_id === "null"));
    if (isNeedCreateFirst) {
      isCreateRef.current = true;
      needPalmSideList = [PALM_RIGHT, PALM_RIGHT, PALM_LEFT, PALM_LEFT];
    }

    try {
      const resultList = [];
      const isVerify =
        action === "verify" ||
        action === "verify-skip" ||
        needPalmSideList.length === 1;

      for (let i = 0; i < needPalmSideList.length; i++) {
        livenessCheckCount = 0;
        let allow_audit = i % 2 === 1;
        if (isDemo && isCreateRef.current) {
          allow_audit = true;
        }
        const needSide = needPalmSideList[i];
        setPalmSide(needSide);

        if (
          !isDemo &&
          needPalmSideList.length > 1 &&
          i >= 1 &&
          needPalmSideList[i] !== needPalmSideList[i - 1]
        ) {
          setShowSwitchLeftAnimation(true);
        }

        setNeedGesture(0);
        setAdjustment(-1);
        setStatus("");
        setTouchPoints(null);
        Module.PalmID_SetConfig("allow_audit", allow_audit ? "true" : "false");

        if (i === 1 || i === 3) {
          await sleep(1000);
        } else {
          await sleep(4000);
        }

        const captureResult = await getCaptureResult(Module, {
          allow_audit,
          needSide,
          nonce,
          isVerify,
        });

        if (!captureResult) {
          return;
        }

        resultList.push(captureResult);
        setProgress((old) => old + 1);
      }

      setLoading(true);
      const palmList = resultList.map((result) => {
        return getPalm64FromResult(Module, result);
      });

      if (isNeedCreateFirst) {
        await createUser(palmList, nonce, {
          isUpdatePalms: isNeedUpdatePalms,
        });

        if (action === "update-palms") {
          navigate("/user");
          return;
        }

        navigate("/verify-registration-success");
        return;
      }

      const identityCommitment = getIdentityCommitment();
      await handleScanAction(action, {
        palmList,
        nonce,
        identityCommitment,
        userReviewStatus,
      });

      if (action === "create" || action === "login") {
        navigate("/user");
      } else if (action === "oauth" && cb) {
        // oauthFlow() already scheduled window.location.href -> /verify-go-back.
        // Navigating here would overwrite that redirect.
        return;
      } else {
        navigate("/verify-success");
      }
    } catch (error) {
      setLoading(false);
      captureError("scan error", error);

      const errorObject = getCodeMessageFromError(error);

      const errorMessage = messageFromError(error, t);
      if (errorMessage.includes("User not found") || errorObject.code === "404") {
        if (cb) {
          removeUser();
          redirectBackWithError("no_user");
          return;
        }
        // logout();
      }

      if (errorObject.code === '1016' || errorObject.code === '1035') {

        if (action === "update-palms") {
          navigate("/result?type=email-used&text=" + errorObject.message);

          return;
        } else if (action === 'email-login') {
          const email = urlParams.get("email");
          navigate("/result?type=additional-required&email=" + email);
        }

        return;
      }
      navigate("/verify-failed", {
        state: {
          error: messageFromError(error, t) || t("common.networkError"),
          isNewCreate: isCreateRef.current,
        },
      });
    } finally {
      setShowLoadWasmMask(false);
    }
    hasStarted.current = false;
  };

  useEffect(() => {
    if (showWorkThrough) {
      preloadWasmToCache();
    }
  }, [showWorkThrough]);

  useEffect(() => {
    // Reset states when component mounts
    setProgress(0);
    setStatus("");
    setAdjustment(-1);
    setNeedGesture(0);
    setScanSide(-1);
    setTouchPoints(null);
    setLoading(false);
    setShowLoadWasmMask(false);
    setShowSwitchLeftAnimation(false);
    needScanningRef.current = true;
    hasStarted.current = false;

    // Start scan if not already started
    if (!hasStarted.current) {
      hasStarted.current = true;
      startScan();
    }

    // Cleanup function to reset refs and clean up resources when component unmounts
    return () => {
      hasStarted.current = false;
      cleanupVideoUtils();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let scanText = t("scan.scanEitherPalm");
  if (palmSide === 1) {
    scanText = t("scan.scanRightPalm");
  } else if (palmSide === 0) {
    scanText = t("scan.scanLeftPalm");
  }

  useEffect(() => {
    needScanningRef.current = !showSwitchLeftAnimation && !showWorkThrough;
  }, [showSwitchLeftAnimation, showWorkThrough]);

  return (
    <div className="bg-[#141517] text-[#fff] flex flex-col h-screen max-h-dvh ">
      <Nav light className="px-3 py-3" right={key === "skip" && <DemoMode />} />
      {isCreateRef.current && (
        <ScanProgress progress={progress / totalProgress} />
      )}

      <div className="flex flex-col flex-1 relative">
        <div className="bg-[#141517] p-3 flex-1 flex justify-center">
          <div className="flex-1 self-stretch inline-flex flex-col justify-center items-center gap-2">
            <div className="self-stretch text-center justify-start text-[#FCFCFC] text-size-24 font-medium leading-loose">
              {scanText}
            </div>

            {isCreateRef.current && progress === 0 && (
              <div className="text-center justify-start text-[#FCFCFC] text-sm font-medium leading-tight">
                {t("scan.scanInstructions")}
              </div>
            )}
          </div>
        </div>
        <div
          className="relative w-full"
          style={{ aspectRatio: videoAspectRatio() }}
        >
          {showLoadWasmMask && (
            <div className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-[5px] bg-[rgba(3,3,3,0.4)]">
              <span className="text-white font-bold text-[20px]">
                {t("common.loading")}
              </span>
            </div>
          )}

          {!showWorkThrough && (
            <PalmVideoFrame
              isGesture={isGesture}
              needGesture={needGesture}
              status={status}
              adjustment={adjustment}
              palmSide={palmSide}
              scanSide={scanSide}
              touchPoints={touchPoints}
            />
          )}
        </div>

        <ScanBottom
          isGesture={isGesture}
          needGesture={needGesture}
          status={status}
          adjustment={adjustment}
          touchPoints={touchPoints}
        />

        <SwitchLeftPalm
          showSwitchLeftAnimation={showSwitchLeftAnimation}
          setShowSwitchLeftAnimation={setShowSwitchLeftAnimation}
        />

        {showWorkThrough && (
          <WorkThrough
            onClick={() => {
              setProgress((old) => old + 1);
              setShowWorkThrough(false);
              if (!hasStarted.current) {
                hasStarted.current = true;
                startScan();
              }
            }}
          />
        )}
        {loading && <VerifyChecking text={t("scan.checkingRequirements")} />}
      </div>
    </div>
  );
};
